import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  Injector,
  ElementRef,
  HostListener,
  forwardRef,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  FormControl,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'select-input',
    imports: [CommonModule, ReactiveFormsModule, TranslateModule],
    templateUrl: './select-input.component.html',
    styleUrls: ['./select-input.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => SelectInputComponent),
            multi: true,
        },
    ]
})
export class SelectInputComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  @Input() label = '';
  @Input() placeholder = '';
  @Input() options: { label: string; value: string }[] = [];
  @Input() searchable = false;
  @Input() searchPlaceholder = 'Buscar...';

  control = new FormControl('');
  searchTerm = '';
  dropdownOpen = false;

  /** Reference to the outer NgControl (e.g. formControlName="country") so we
   *  can reflect its real validity/touched state. Resolved lazily via the
   *  Injector to avoid a circular DI error with NG_VALUE_ACCESSOR. */
  private ngControl: NgControl | null = null;

  private onChange = (_: any) => {};
  private onTouched = () => {};

  constructor(
    private injector: Injector,
    private elementRef: ElementRef<HTMLElement>
  ) {}

  ngOnInit(): void {
    this.ngControl = this.injector.get(NgControl, null);
    this.control.valueChanges.subscribe((val) => {
      this.onChange(val);
    });
  }

  get selectedLabel(): string {
    const selected = this.options.find((opt) => opt.value === this.control.value);
    return selected?.label ?? '';
  }

  get filteredOptions(): { label: string; value: string }[] {
    const query = this.searchTerm.trim().toLowerCase();
    if (!query) {
      return this.options;
    }

    return this.options.filter((opt) =>
      opt.label.toLowerCase().includes(query)
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.searchable || !this.dropdownOpen) {
      return;
    }

    const target = event.target as Node | null;
    if (target && !this.elementRef.nativeElement.contains(target)) {
      this.closeDropdown();
      this.onTouched();
    }
  }

  onBlur(): void {
    this.onTouched();
  }

  toggleDropdown(): void {
    if (this.control.disabled) {
      return;
    }

    this.dropdownOpen = !this.dropdownOpen;
    if (!this.dropdownOpen) {
      this.onTouched();
      this.searchTerm = '';
    }
  }

  selectOption(option: { label: string; value: string }): void {
    this.control.setValue(option.value);
    this.closeDropdown();
    this.onTouched();
  }

  onSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm = input.value;
  }

  closeDropdown(): void {
    this.dropdownOpen = false;
    this.searchTerm = '';
  }

  get showError(): boolean {
    return !!this.ngControl && !!this.ngControl.invalid && !!this.ngControl.touched;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      const currentValue = this.control.value;
      const validValues = this.options.map((opt) => opt.value);
      if (currentValue && !validValues.includes(currentValue)) {
        this.control.setValue('', { emitEvent: false });
      }
    }
  }

  writeValue(value: any): void {
    this.control.setValue(value, { emitEvent: false });
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) this.control.disable();
    else this.control.enable();
  }
}
